/* import __COLOCATED_TEMPLATE__ from './predicates-bridge.hbs'; */
/* RESPONSIBLE TEAM: team-fin-qa */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import { registerDestructor } from '@ember/destroyable';
import { action } from '@ember/object';
import PredicateGroup from 'predicates/models/predicate-group';

interface Signature {
  Args: {};
}

export default class PredicatesBridge extends Component<Signature> {
  @service declare store: $TSFixMe;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare appService: $TSFixMe;

  @tracked showPredicatesEditorSideSheet = false;
  @tracked predicateGroup?: $TSFixMe;
  @tracked finInvolvedPredicates: unknown[] = [];

  constructor(owner: unknown, args: {}) {
    super(owner, args);

    let unsubscribeOpen = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'fin-qa-monitor:predicates:open',
      async (data) => {
        let eventData = data as {
          predicates?: unknown[];
          scorecardId?: string | null;
        };

        // Clear predicateGroup to show loading state and open the side sheet immediately
        this.predicateGroup = undefined;
        this.showPredicatesEditorSideSheet = true;

        try {
          await Promise.all([
            this.store.findAll('conversation-attributes/descriptor'),
            this.appService.app.reload(),
            this.store.findAll('ai-agent/guidelines'),
            this.store.findAll('ai-agent/escalation-rule'),
            ...(this.appService.app.currentAdmin.canAccessProductSettings
              ? [this.store.findAll('qa-monitor/cx-score-reason').catch((_e: unknown) => undefined)]
              : []),
            ...(this.appService.app.canUseFeature('ai_topics')
              ? [
                  // Topics/subtopics are optional — continue if endpoints return 404
                  this.store.findAll('qa-monitor/ai-topic').catch(() => undefined),
                  this.store.findAll('qa-monitor/ai-subtopic').catch(() => undefined),
                ]
              : []),
          ]);
        } catch (e: unknown) {
          // Close the side sheet if core data loading fails
          this.showPredicatesEditorSideSheet = false;
          return;
        }

        let nonRoutingGuidelineValues = new Set(
          this.store
            .peekAll('ai-agent/guidelines')
            .filter((g: $TSFixMe) => g.category !== 'routing')
            .map((g: $TSFixMe) => `guideline_${g.id}`),
        );

        let expandGuidanceOnly = (preds: $TSFixMe[]): $TSFixMe[] =>
          preds.map((p: $TSFixMe) => {
            if (p.predicates) {
              return { ...p, predicates: expandGuidanceOnly(p.predicates) };
            }
            if (
              p.attribute === 'conversation.guidance_applied' &&
              typeof p.value === 'string' &&
              p.value.startsWith('guideline_') &&
              nonRoutingGuidelineValues.has(p.value)
            ) {
              return { ...p, attribute: 'conversation.guidance_only' };
            }
            return p;
          });

        let allPredicates: unknown[] = expandGuidanceOnly(eventData.predicates || []);

        // Implicit predicates are derived from the scorecard config and displayed as
        // read-only chips in React. Strip them from the editable set so they don't
        // appear in the predicates editor. Covers team/admin assignee predicates
        // (teammate scorecards) and fin_involved (Fin scorecard). When no scorecard is
        // selected, fin_involved is NOT implicit — users opt in via the Add filter
        // picker, so it should remain in the editable set.
        let implicitAttributes = new Set<string>([
          'conversation.team_assignee_id',
          'conversation.admin_assignee_id',
        ]);
        if (eventData.scorecardId != null) {
          implicitAttributes.add('conversation.fin_involved');
        }

        let isImplicitPredicate = (p: $TSFixMe) => {
          if (implicitAttributes.has(p.attribute)) {
            return true;
          }
          // Compound OR predicates generated for mixed team+admin targets
          if (p.type === 'or' && Array.isArray(p.predicates)) {
            return p.predicates.every((sub: $TSFixMe) => implicitAttributes.has(sub.attribute));
          }
          return false;
        };

        this.finInvolvedPredicates = allPredicates.filter(isImplicitPredicate);
        let editablePredicates = allPredicates.filter((p: $TSFixMe) => !isImplicitPredicate(p));

        this.predicateGroup = this.store.createFragment('predicates/predicate-group', {
          predicates: PredicateGroup.convertRawPredicates(this.store, editablePredicates),
        });
      },
    );

    let unsubscribeClose = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'fin-qa-monitor:predicates:close',
      () => {
        this.showPredicatesEditorSideSheet = false;
      },
    );

    registerDestructor(this, () => {
      unsubscribeOpen();
      unsubscribeClose();
    });
  }

  @action closePredicatesEditorSideSheet() {
    this.showPredicatesEditorSideSheet = false;
    this.reactTeammateAppIsland.dispatchBridgeEvent('teammate-app:event', {
      identifier: 'fin-qa-monitor:predicates:closed',
      data: {},
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'FinQaMonitor::PredicatesBridge': typeof PredicatesBridge;
    'fin-qa-monitor/predicates-bridge': typeof PredicatesBridge;
  }
}
